import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { LazyImage } from 'components/LazyImage';
import { colorsV2 } from 'style/colors-v2';
import { Typography } from 'components/DesignSystemV2/Typography';
import { useInView } from 'react-intersection-observer';
import PopupButton from '@dolstaff/shared/es/PopupButton';
import ResultCard from 'shared/HallOfFameV2/ResultCard';
import { OutlineInterfaceCloss } from '@dolstaff/shared/es/icons';
import { IconCourseArrowRight } from 'components/Icon/SVGIcons';

const ViewResultButton = styled.button`
  padding: 12px;
  background-color: transparent;
  color: ${colorsV2.white100};
  border: 0;
  outline: none;
  cursor: pointer;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  font: inherit;
  display: flex;
  align-items: center;
  gap: 4px;
  visibility: hidden;
  opacity: 0;
  transition: all 0.3s ease;
`;

const ItemWrapper = styled(motion.div)`
  padding-top: 44px;
  width: 100%;
  position: relative;
`;

const ImageWrapper = styled.div`
  max-width: 100%;
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  aspect-ratio: 258 / 192;
  border: 1px solid ${colorsV2.neutral20};
  margin-bottom: 16px;

  &:hover {
    &::after {
      opacity: 1;
      visibility: visible;
    }

    ${ViewResultButton} {
      opacity: 1;
      visibility: visible;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${colorsV2.blackTransparent40};
    z-index: 1;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TextWrapper = styled.div`
  padding: 0 16px;
`;

const Name = styled(Typography)`
  margin-bottom: 8px;
`;

const School = styled(Typography)`
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Score = styled.div`
  position: absolute;
  left: 12px;
  bottom: 12px;
  padding: 8px 16px 14px;
  background: ${colorsV2.white80};
  border-radius: 12px;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 3px;
  backdrop-filter: blur(32px);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  padding: 8px;
  background-color: white;
  color: black;
  border: 0;
  outline: none;
  cursor: pointer;
  border-radius: 8px;
`;

const PopupWrapper = styled.div`
  position: relative;
`;

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};

const HonorRollListItem = ({
  bangVangAvatar,
  bangVangDisplayName,
  bangVangOccupation,
  bangVangCertificate,
  overallScore,
  program,
  index
}) => {
  const { ref, inView } = useInView({ triggerOnce: true });

  return (
    <PopupButton
      hideCloseButton
      hideOnBackdropClick
      isCentered
      zIndex={600}
      style={{
        width: '100%',
        maxWidth: '500px',
        padding: '14px 16px'
      }}
      trigger={({ toggleContent }) => (
        <ItemWrapper
          ref={ref}
          variants={itemVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          transition={{
            duration: 0.5,
            delay: 0.1 * (index % 8),
            type: 'spring',
            stiffness: 100,
            damping: 15
          }}
        >
          <ImageWrapper>
            <ViewResultButton onClick={toggleContent}>
              Xem result <IconCourseArrowRight />
            </ViewResultButton>
            <LazyImage
              handle={bangVangAvatar}
              alt={`${bangVangDisplayName} ${overallScore} ${program}`}
            />
            <Score>
              <Typography variant="bold/32-39" color={colorsV2.primary100}>
                {overallScore}
              </Typography>
              <Typography variant="bold/16-24" color={colorsV2.neutral180}>
                {program}
              </Typography>
            </Score>
          </ImageWrapper>
          <TextWrapper>
            <Name variant="semi-bold/24-32" color={colorsV2.neutral200}>
              {bangVangDisplayName}
            </Name>
            <School variant="regular/16-24" color={colorsV2.neutral120}>
              {bangVangOccupation}
            </School>
          </TextWrapper>
        </ItemWrapper>
      )}
    >
      {({ toggleContent }) => (
        <PopupWrapper>
          <CloseBtn onClick={toggleContent}>
            <OutlineInterfaceCloss size={24} width={24} height={24} />
          </CloseBtn>
          <ResultCard
            full
            name={bangVangDisplayName}
            occupation={bangVangOccupation}
            band={overallScore}
            certificate={bangVangCertificate}
          />
        </PopupWrapper>
      )}
    </PopupButton>
  );
};

export default HonorRollListItem;
